import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import color from 'utils/colors';
import AddIcon from '@mui/icons-material/Add';
import { Delete, Edit } from '@mui/icons-material';
import { styled } from '@mui/styles';
import TableCell from '@mui/material/TableCell';
import { useDispatch } from 'react-redux';
import { appSetError } from 'commons/commons.actions';
import { isZippedFile } from 'utils/functions';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

function StepThree(props) {
  const { attachmentsArray, setAttachmentsArray } = props;

  const dispatch = useDispatch();

  const handleFileInputClick = () => {
    const fileInput = document.getElementById('fileInputMultiple');
    if (fileInput) {
      fileInput.click();
    }
  };

  const handleFileChange = (event) => {
    const { files } = event.target;

    if (!files || files.length === 0) return;

    const validFiles = [];
    const invalidFiles = [];

    // Validate each selected file
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      if (file && !isZippedFile(file.name)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    }

    // Add valid files to the table
    if (validFiles.length > 0) {
      const newAttachments = validFiles.map((file) => ({
        publicAtt: file,
        privateAtt: null,
      }));
      setAttachmentsArray([...attachmentsArray, ...newAttachments]);
    }

    // Show error for invalid files
    if (invalidFiles.length > 0) {
      dispatch(
        appSetError({
          msg: `Los siguientes archivos no pueden ser comprimidos: ${invalidFiles.join(', ')}`,
        }),
      );
    }

    // Reset input to allow selecting same files again
    // eslint-disable-next-line no-param-reassign
    event.target.value = '';
  };

  const removeFileRow = (idx) => {
    const attachmentsRows = [...attachmentsArray];
    attachmentsRows.splice(idx, 1);
    setAttachmentsArray(attachmentsRows);
  };

  const editFileRow = (idx) => {
    const fileInput = document.getElementById(`fileInputEdit-${idx}`);
    if (fileInput) {
      fileInput.click();
    }
  };

  const handleEditFileChange = (event, idx) => {
    const { files } = event.target;

    if (!files || files.length === 0) return;

    const file = files[0];

    if (file && !isZippedFile(file.name)) {
      const updatedArray = attachmentsArray.map((item, index) =>
        index === idx ? { publicAtt: file, privateAtt: null } : item,
      );
      setAttachmentsArray(updatedArray);
    } else {
      dispatch(appSetError({ msg: 'El documento no puede ser un archivo comprimido' }));
    }

    // Reset input
    // eslint-disable-next-line no-param-reassign
    event.target.value = '';
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          mb: 2,
        }}>
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 'bold',
            color: color.blue,
            textTransform: 'uppercase',
            mr: 1,
          }}>
          CARGAR
        </Typography>
        <Typography
          sx={{
            fontWeight: 'regular',
            color: color.blue,
            fontSize: 16,
          }}>
          Archivos adjuntos
        </Typography>
        <input
          accept="application/pdf"
          id="fileInputMultiple"
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <IconButton
          color="primary"
          aria-label="upload picture"
          component="span"
          onClick={handleFileInputClick}>
          <AddIcon
            sx={{
              color: color.white,
              backgroundColor: color.blue,
              borderRadius: 3,
            }}
          />
        </IconButton>
      </Box>
      <TableContainer component={Paper}>
        <Table sx={{ maxWidth: 700 }} size="small" aria-label="customized table">
          <TableHead>
            <TableRow>
              <StyledTableCell />
              <StyledTableCell>Adjunto (versión pública)</StyledTableCell>
              {/* INFO: Cliente requiere retirar la opción de agregar archivos confidenciales
              <StyledTableCell align="right">Versión confidencial</StyledTableCell> */}
              <StyledTableCell align="center">Acciones</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {attachmentsArray.map((attachment, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <StyledTableRow key={`${attachment.publicAtt?.name} ${idx}`}>
                <StyledTableCell component="th" scope="row">
                  {`${idx + 1}.`}
                </StyledTableCell>
                <StyledTableCell>{attachmentsArray[idx].publicAtt?.name}</StyledTableCell>
                {/* INFO: Cliente requiere retirar la opción de agregar archivos confidenciales
                <StyledTableCell align="center">
                  {attachmentsArray[idx].privateAtt?.name || '-'}
                </StyledTableCell> */}
                <StyledTableCell align="right">
                  <input
                    accept="application/pdf"
                    id={`fileInputEdit-${idx}`}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={(e) => handleEditFileChange(e, idx)}
                  />
                  <IconButton
                    sx={{ color: color.blue }}
                    aria-label="edit attachment"
                    component="span"
                    onClick={() => editFileRow(idx)}>
                    <Edit />
                  </IconButton>
                  <IconButton
                    sx={{ color: color.blue }}
                    aria-label="delete attachment"
                    component="span"
                    onClick={() => removeFileRow(idx)}>
                    <Delete />
                  </IconButton>
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

StepThree.propTypes = {
  attachmentsArray: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setAttachmentsArray: PropTypes.func.isRequired,
};

export default StepThree;
